import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  Table, Sun, Moon, Calendar as CalendarIcon, Sunrise, Sunset,
  Eye, EyeOff, Clock
} from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { useI18n } from '../context/I18nContext';

const MOON_PHASE_KEYS = {
  'New Moon': 'moon.newMoon',
  'Waxing Crescent': 'moon.waxingCrescent',
  'First Quarter': 'moon.firstQuarter',
  'Waxing Gibbous': 'moon.waxingGibbous',
  'Full Moon': 'moon.fullMoon',
  'Waning Gibbous': 'moon.waningGibbous',
  'Last Quarter': 'moon.lastQuarter',
  'Waning Crescent': 'moon.waningCrescent',
};

const EphemerisTables = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Calculate sun times for a given date
  const calculateSunTimes = (date, lat, lng) => {
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
    const declination = 23.45 * Math.sin((360 / 365) * (dayOfYear - 81) * Math.PI / 180);
    
    const latRad = lat * Math.PI / 180;
    const decRad = declination * Math.PI / 180;
    
    // Calculate hour angle for different sun altitudes
    const calculateHourAngle = (altitude) => {
      const altRad = altitude * Math.PI / 180;
      const cosHA = (Math.sin(altRad) - Math.sin(latRad) * Math.sin(decRad)) / 
                    (Math.cos(latRad) * Math.cos(decRad));
      if (Math.abs(cosHA) > 1) return null;
      return Math.acos(cosHA) * 180 / Math.PI;
    };

    const solarNoon = 12.0 - lng / 15.0;
    
    const hoursToTime = (h) => {
      if (h === null) return '--:--';
      h = ((h % 24) + 24) % 24;
      const hours = Math.floor(h);
      const minutes = Math.floor((h - hours) * 60);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    // Sunrise/Sunset (0°)
    const sunriseHA = calculateHourAngle(0);
    const sunrise = sunriseHA ? solarNoon - sunriseHA / 15 : null;
    const sunset = sunriseHA ? solarNoon + sunriseHA / 15 : null;

    // Civil twilight (-6°)
    const civilHA = calculateHourAngle(-6);
    const civilDawn = civilHA ? solarNoon - civilHA / 15 : null;
    const civilDusk = civilHA ? solarNoon + civilHA / 15 : null;

    // Nautical twilight (-12°)
    const nauticalHA = calculateHourAngle(-12);
    const nauticalDawn = nauticalHA ? solarNoon - nauticalHA / 15 : null;
    const nauticalDusk = nauticalHA ? solarNoon + nauticalHA / 15 : null;

    // Astronomical twilight (-18°)
    const astroHA = calculateHourAngle(-18);
    const astroDawn = astroHA ? solarNoon - astroHA / 15 : null;
    const astroDusk = astroHA ? solarNoon + astroHA / 15 : null;

    // Golden hour (6°)
    const goldenHA = calculateHourAngle(6);
    const goldenMorningEnd = goldenHA ? solarNoon - goldenHA / 15 : null;
    const goldenEveningStart = goldenHA ? solarNoon + goldenHA / 15 : null;

    // Day length
    const dayLength = sunrise && sunset ? sunset - sunrise : 0;

    return {
      date,
      sunrise: hoursToTime(sunrise),
      sunset: hoursToTime(sunset),
      solarNoon: hoursToTime(solarNoon),
      civilDawn: hoursToTime(civilDawn),
      civilDusk: hoursToTime(civilDusk),
      nauticalDawn: hoursToTime(nauticalDawn),
      nauticalDusk: hoursToTime(nauticalDusk),
      astroDawn: hoursToTime(astroDawn),
      astroDusk: hoursToTime(astroDusk),
      goldenMorningEnd: hoursToTime(goldenMorningEnd),
      goldenEveningStart: hoursToTime(goldenEveningStart),
      dayLength: dayLength ? `${Math.floor(dayLength)}h ${Math.round((dayLength % 1) * 60)}m` : '--'
    };
  };

  // Calculate moon phase for a given date
  const calculateMoonPhase = (date) => {
    const reference = new Date(2000, 0, 6, 18, 14, 0);
    const daysSince = (date - reference) / 86400000;
    const lunarCycle = 29.530588853;
    const phase = ((daysSince % lunarCycle) + lunarCycle) % lunarCycle / lunarCycle;
    
    const phaseNames = [
      { threshold: 0.0625, name: 'New Moon', icon: '🌑' },
      { threshold: 0.1875, name: 'Waxing Crescent', icon: '🌒' },
      { threshold: 0.3125, name: 'First Quarter', icon: '🌓' },
      { threshold: 0.4375, name: 'Waxing Gibbous', icon: '🌔' },
      { threshold: 0.5625, name: 'Full Moon', icon: '🌕' },
      { threshold: 0.6875, name: 'Waning Gibbous', icon: '🌖' },
      { threshold: 0.8125, name: 'Last Quarter', icon: '🌗' },
      { threshold: 0.9375, name: 'Waning Crescent', icon: '🌘' },
      { threshold: 1.0, name: 'New Moon', icon: '🌑' }
    ];

    let phaseName = phaseNames[0];
    for (const p of phaseNames) {
      if (phase < p.threshold) {
        phaseName = p;
        break;
      }
    }

    const illumination = Math.round((1 - Math.cos(2 * Math.PI * phase)) / 2 * 100);

    return {
      phase: Math.round(phase * 100) / 100,
      name: phaseName.name,
      icon: phaseName.icon,
      illumination
    };
  };

  // Generate week data
  const weekData = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(selectedDate, i);
      return {
        ...calculateSunTimes(date, latitude, longitude),
        moon: calculateMoonPhase(date)
      };
    });
  }, [selectedDate, latitude, longitude]);

  // Generate month moon phases
  const monthMoonData = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => ({
      date: day,
      ...calculateMoonPhase(day)
    }));
  }, [selectedDate]);

  // Find special moon dates
  const specialMoonDates = useMemo(() => {
    const newMoons = monthMoonData.filter(d => d.name === 'New Moon');
    const fullMoons = monthMoonData.filter(d => d.name === 'Full Moon');
    return { newMoons, fullMoons };
  }, [monthMoonData]);

  const todaySun = calculateSunTimes(selectedDate, latitude, longitude);
  const todayMoon = calculateMoonPhase(selectedDate);

  return (
    <div className="space-y-4">
      {/* Date Selector */}
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-white flex items-center gap-2">
          <Table className="w-5 h-5 text-cyan-400" />
          {t('ephemeris.title')}
        </h3>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="border-white/10 bg-slate-800" data-testid="ephemeris-date-picker">
              <CalendarIcon className="w-4 h-4 mr-2" />
              {format(selectedDate, 'MMM d, yyyy')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-slate-900 border-white/10">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setCalendarOpen(false);
                }
              }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <Tabs defaultValue="today" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="today" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="eph-tab-today">
            {t('ephemeris.today')}
          </TabsTrigger>
          <TabsTrigger value="week" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="eph-tab-week">
            {t('ephemeris.week')}
          </TabsTrigger>
          <TabsTrigger value="moon" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="eph-tab-moon">
            {t('ephemeris.moonCal')}
          </TabsTrigger>
        </TabsList>

        {/* Today Tab */}
        <TabsContent value="today" className="mt-4 space-y-4">
          {/* Twilight Times */}
          <Card className="bg-slate-900/50 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-500" />
                {t('ephemeris.twilight')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Astronomical */}
              <div className="flex items-center justify-between p-2 rounded bg-indigo-500/10">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm text-slate-300">{t('ephemeris.astronomical')}</span>
                </div>
                <div className="font-mono text-sm">
                  <span className="text-indigo-400">{todaySun.astroDawn}</span>
                  <span className="text-slate-500 mx-2">—</span>
                  <span className="text-indigo-400">{todaySun.astroDusk}</span>
                </div>
              </div>
              
              {/* Nautical */}
              <div className="flex items-center justify-between p-2 rounded bg-blue-500/10">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-slate-300">{t('ephemeris.nautical')}</span>
                </div>
                <div className="font-mono text-sm">
                  <span className="text-blue-400">{todaySun.nauticalDawn}</span>
                  <span className="text-slate-500 mx-2">—</span>
                  <span className="text-blue-400">{todaySun.nauticalDusk}</span>
                </div>
              </div>
              
              {/* Civil */}
              <div className="flex items-center justify-between p-2 rounded bg-cyan-500/10">
                <div className="flex items-center gap-2">
                  <Sunrise className="w-4 h-4 text-cyan-400" />
                  <span className="text-sm text-slate-300">{t('ephemeris.civil')}</span>
                </div>
                <div className="font-mono text-sm">
                  <span className="text-cyan-400">{todaySun.civilDawn}</span>
                  <span className="text-slate-500 mx-2">—</span>
                  <span className="text-cyan-400">{todaySun.civilDusk}</span>
                </div>
              </div>
              
              {/* Sunrise/Sunset */}
              <div className="flex items-center justify-between p-2 rounded bg-amber-500/10">
                <div className="flex items-center gap-2">
                  <Sun className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-slate-300">{t('ephemeris.daylight')}</span>
                </div>
                <div className="font-mono text-sm">
                  <span className="text-amber-500">{todaySun.sunrise}</span>
                  <span className="text-slate-500 mx-2">—</span>
                  <span className="text-orange-500">{todaySun.sunset}</span>
                </div>
              </div>

              <div className="pt-2 border-t border-white/10 flex justify-between">
                <span className="text-sm text-slate-400">{t('sun.dayLength')}</span>
                <span className="font-mono text-white">{todaySun.dayLength}</span>
              </div>
            </CardContent>
          </Card>

          {/* Moon Today */}
          <Card className="bg-slate-900/50 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300 flex items-center gap-2">
                <Moon className="w-4 h-4 text-indigo-400" />
                {t('ephemeris.moonToday')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <span className="text-4xl">{todayMoon.icon}</span>
                <div>
                  <p className="text-white font-medium">{t(MOON_PHASE_KEYS[todayMoon.name])}</p>
                  <p className="text-slate-400 text-sm">{todayMoon.illumination}% {t('moon.illuminated')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Week Tab */}
        <TabsContent value="week" className="mt-4">
          <Card className="bg-slate-900/50 border-white/10">
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-900">
                    <tr className="border-b border-white/10">
                      <th className="p-2 text-left text-slate-400 font-normal">{t('ephemeris.date')}</th>
                      <th className="p-2 text-center text-amber-500 font-normal">↑</th>
                      <th className="p-2 text-center text-orange-500 font-normal">↓</th>
                      <th className="p-2 text-center text-slate-400 font-normal">{t('ephemeris.length')}</th>
                      <th className="p-2 text-center text-slate-400 font-normal">{t('ephemeris.moon')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekData.map((day, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-2 text-white">
                          {format(day.date, 'EEE, MMM d')}
                        </td>
                        <td className="p-2 text-center font-mono text-amber-500">{day.sunrise}</td>
                        <td className="p-2 text-center font-mono text-orange-500">{day.sunset}</td>
                        <td className="p-2 text-center font-mono text-slate-300">{day.dayLength}</td>
                        <td className="p-2 text-center">
                          <span title={`${t(MOON_PHASE_KEYS[day.moon.name])} - ${day.moon.illumination}%`}>
                            {day.moon.icon}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Moon Calendar Tab */}
        <TabsContent value="moon" className="mt-4 space-y-4">
          {/* Special Dates */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-slate-900/50 border-white/10">
              <CardContent className="p-3">
                <p className="text-xs text-slate-500 mb-2">{t('ephemeris.newMoons')}</p>
                {specialMoonDates.newMoons.length > 0 ? (
                  specialMoonDates.newMoons.map((d, i) => (
                    <p key={i} className="font-mono text-sm text-white">
                      🌑 {format(d.date, 'MMM d')}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">{t('ephemeris.noneThisMonth')}</p>
                )}
              </CardContent>
            </Card>
            <Card className="bg-slate-900/50 border-white/10">
              <CardContent className="p-3">
                <p className="text-xs text-slate-500 mb-2">{t('ephemeris.fullMoons')}</p>
                {specialMoonDates.fullMoons.length > 0 ? (
                  specialMoonDates.fullMoons.map((d, i) => (
                    <p key={i} className="font-mono text-sm text-white">
                      🌕 {format(d.date, 'MMM d')}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-500 text-sm">{t('ephemeris.noneThisMonth')}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Moon Grid */}
          <Card className="bg-slate-900/50 border-white/10">
            <CardContent className="p-3">
              <div className="grid grid-cols-7 gap-1">
                {t('ephemeris.weekDaysShort').split(',').map((d, i) => (
                  <div key={i} className="text-center text-xs text-slate-500 py-1">{d}</div>
                ))}
                {/* Padding for first day */}
                {Array.from({ length: startOfMonth(selectedDate).getDay() }, (_, i) => (
                  <div key={`pad-${i}`} />
                ))}
                {monthMoonData.map((day, i) => (
                  <div
                    key={i}
                    className="text-center p-1 rounded hover:bg-white/5 cursor-default"
                    title={`${format(day.date, 'MMM d')}: ${t(MOON_PHASE_KEYS[day.name])} (${day.illumination}%)`}
                  >
                    <span className="text-lg">{day.icon}</span>
                    <p className="text-xs text-slate-500">{format(day.date, 'd')}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EphemerisTables;
